import React, { useState, useCallback, useMemo } from 'react';
import { 
  X, Check, Package, Globe, ShoppingCart, ChevronRight, ChevronLeft,
  CreditCard, Users, Calendar, QrCode, BarChart3, Tag, ScanLine, FolderTree,
  Boxes, Share2, Layout, FileText, Truck, Receipt, UserPlus, Heart,
  Wallet, Crown, Smartphone, CheckCircle2, Play, SkipForward
} from 'lucide-react';

const BRAND = {
  navy: '#0B1F4A',
  blue: '#1A56DB',
  blueLight: '#60A5FA',
  green: '#22c55e',
  greenLight: '#4ade80',
  amber: '#f59e0b',
} as const;

type ModuleId = 'productos' | 'tienda' | 'pedidos' | 'ventas' | 'clientes' | 'cobros' | 'checkin';

interface Module {
  id: ModuleId;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  duration: string;
  steps: ModuleStep[];
}

interface ModuleStep {
  id: string;
  title: string;
  description: string;
  content: React.ReactNode;
  hotspotLabel?: string;
}

interface GuidedWalkthroughProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToQuickDemo: () => void;
}

const formatCOP = (n: number) => 
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);

const MOCK_PRODUCTS = [
  { name: 'Camiseta Premium', sku: 'CAM-4521', barcode: '7701234567890', price: 45000, cost: 25000, stock: 15, category: 'Ropa', image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=100&h=100&fit=crop' },
  { name: 'Zapatos Deportivos', sku: 'ZAP-8734', barcode: '7701234567891', price: 189000, cost: 95000, stock: 8, category: 'Calzado', image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=100&h=100&fit=crop' },
  { name: 'Reloj Clásico', sku: 'REL-2198', barcode: '7701234567892', price: 320000, cost: 180000, stock: 5, category: 'Accesorios', image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=100&h=100&fit=crop' },
];

const MOCK_CLIENTS = [
  { name: 'María González', phone: '+57 300 123 4567', email: 'maria@email.com', totalCompras: 3, ultimaCompra: '2024-10-15', segment: 'Frecuente' },
  { name: 'Carlos Rodríguez', phone: '+57 311 987 6543', email: 'carlos@email.com', totalCompras: 1, ultimaCompra: '2024-10-20', segment: 'Nuevo' },
  { name: 'Ana Martínez', phone: '+57 320 456 7890', email: 'ana@email.com', totalCompras: 12, ultimaCompra: '2024-10-25', segment: 'VIP' },
];

const MOCK_ORDERS = [
  { id: 'PED-4521', client: 'María González', product: 'Camiseta Premium', qty: 2, total: 107100, status: 'pending', paymentStatus: 'proof_uploaded' },
  { id: 'PED-4520', client: 'Carlos Rodríguez', product: 'Zapatos Deportivos', qty: 1, total: 224910, status: 'confirmed', paymentStatus: 'paid' },
];

const PhoneMockup: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => (
  <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl max-w-[300px] mx-auto">
    <div className="bg-white rounded-[1.5rem] overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
          <p className="text-xs font-medium text-gray-600 text-center">{title}</p>
        </div>
      )}
      <div className="p-3 min-h-[300px]">
        {children}
      </div>
    </div>
  </div>
);

const Tooltip: React.FC<{ children: React.ReactNode; label: string }> = ({ children, label }) => (
  <div className="relative">
    <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full z-10">
      <div className="bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap animate-bounce">
        {label}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" />
      </div>
    </div>
    <div className="ring-2 ring-blue-400 ring-offset-2 rounded-lg animate-pulse">
      {children}
    </div>
  </div>
);

const createProductosSteps = (): ModuleStep[] => [
  {
    id: 'productos-intro',
    title: 'Bienvenido a Productos',
    description: 'Aquí gestionas todo tu inventario. Vamos a crear un producto paso a paso.',
    hotspotLabel: '¡Empecemos!',
    content: (
      <PhoneMockup title="Productos">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Mis Productos</h3>
            <Tooltip label="Click aquí">
              <button className="px-3 py-1.5 text-xs font-medium text-white rounded-lg" style={{ backgroundColor: BRAND.blue }}>
                + Nuevo
              </button>
            </Tooltip>
          </div>
          <div className="space-y-2">
            {MOCK_PRODUCTS.slice(0, 2).map((p, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <img src={p.image} alt={p.name} className="w-10 h-10 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">{p.name}</p>
                  <p className="text-[10px] text-gray-500">{p.stock} uds</p>
                </div>
                <p className="text-xs font-semibold text-gray-700">{formatCOP(p.price)}</p>
              </div>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'productos-crear',
    title: 'Crear nuevo producto',
    description: 'Ingresa el nombre y el sistema genera automáticamente un código SKU único.',
    hotspotLabel: 'Escribe el nombre',
    content: (
      <PhoneMockup title="Nuevo Producto">
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-700">Nombre del producto</label>
            <Tooltip label="Auto SKU se genera aquí">
              <input
                type="text"
                value="Bolso de Cuero"
                readOnly
                className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
              />
            </Tooltip>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-blue-600" />
              <div>
                <p className="text-[10px] text-blue-600 font-medium">SKU generado</p>
                <p className="text-sm font-mono font-bold text-blue-800">BOL-7834</p>
              </div>
              <span className="ml-auto text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Auto ✓</span>
            </div>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'productos-barcode',
    title: 'Código de barras',
    description: 'Escanea el código de barras del producto o ingresalo manualmente. Perfecto para tiendas de retail.',
    hotspotLabel: 'Escanear o ingresar',
    content: (
      <PhoneMockup title="Código de Barras">
        <div className="space-y-3">
          <div className="text-center p-4 border-2 border-dashed border-gray-200 rounded-xl">
            <ScanLine className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-xs text-gray-500">Toca para escanear</p>
          </div>
          <div className="text-center text-xs text-gray-400">o</div>
          <Tooltip label="Ingresa manualmente">
            <input
              type="text"
              value="7701234567893"
              readOnly
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-center font-mono"
            />
          </Tooltip>
          <div className="flex items-center justify-center gap-2 p-2 bg-green-50 rounded-lg">
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium">Código válido</span>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'productos-categorias',
    title: 'Categorías',
    description: 'Organiza tus productos en categorías para encontrarlos más rápido.',
    hotspotLabel: 'Selecciona categoría',
    content: (
      <PhoneMockup title="Categoría">
        <div className="space-y-3">
          <p className="text-xs text-gray-500">Selecciona una categoría:</p>
          <div className="space-y-2">
            {['Ropa', 'Calzado', 'Accesorios', 'Electrónica'].map((cat, i) => (
              <Tooltip key={cat} label={i === 2 ? 'Seleccionar' : ''}>
                <button 
                  className={`w-full flex items-center gap-2 p-2.5 rounded-lg border text-left transition-colors ${
                    i === 2 ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <FolderTree className={`w-4 h-4 ${i === 2 ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className={`text-sm ${i === 2 ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{cat}</span>
                  {i === 2 && <Check className="w-4 h-4 text-blue-600 ml-auto" />}
                </button>
              </Tooltip>
            ))}
          </div>
          <button className="w-full text-xs text-blue-600 font-medium py-2">
            + Crear nueva categoría
          </button>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'productos-precios',
    title: 'Precio y costo',
    description: 'Define el precio de venta y costo. El sistema calcula automáticamente tu margen de ganancia.',
    hotspotLabel: 'Revisa el margen',
    content: (
      <PhoneMockup title="Precios">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">Precio venta</label>
              <input
                type="text"
                value="$125.000"
                readOnly
                className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Costo</label>
              <input
                type="text"
                value="$70.000"
                readOnly
                className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
              />
            </div>
          </div>
          <Tooltip label="Margen calculado">
            <div className="p-3 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-center justify-between">
                <span className="text-xs text-green-700">Margen de ganancia</span>
                <span className="text-sm font-bold text-green-700">$55.000 (44%)</span>
              </div>
              <div className="mt-2 h-2 bg-green-200 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full" style={{ width: '44%' }} />
              </div>
            </div>
          </Tooltip>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'productos-inventario',
    title: 'Stock e inventario',
    description: 'Registra las unidades disponibles. Recibe alertas cuando el stock esté bajo.',
    hotspotLabel: 'Configura alertas',
    content: (
      <PhoneMockup title="Inventario">
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-700">Stock disponible</label>
            <div className="flex items-center gap-2 mt-1">
              <button className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 font-bold">−</button>
              <input
                type="text"
                value="25"
                readOnly
                className="flex-1 px-3 py-2 text-sm text-center border border-gray-200 rounded-lg bg-white font-semibold"
              />
              <button className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 font-bold">+</button>
            </div>
          </div>
          <Tooltip label="Activa alertas">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2">
                <Boxes className="w-4 h-4 text-amber-600" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-amber-800">Alerta de stock bajo</p>
                  <p className="text-[10px] text-amber-600">Notificar cuando queden menos de:</p>
                </div>
                <input
                  type="text"
                  value="5"
                  readOnly
                  className="w-12 px-2 py-1 text-sm text-center border border-amber-200 rounded bg-white"
                />
              </div>
            </div>
          </Tooltip>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <BarChart3 className="w-4 h-4" />
            <span>Última entrada: Hace 3 días (+10 uds)</span>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'productos-guardado',
    title: '¡Producto creado!',
    description: 'Tu producto está listo. Ya aparece en tu inventario y puede publicarse en la tienda online.',
    content: (
      <PhoneMockup title="Producto Guardado">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">¡Producto creado!</p>
            <p className="text-xs text-gray-500 mt-1">Bolso de Cuero · BOL-7834</p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{formatCOP(125000)}</p>
              <p className="text-[10px] text-gray-500">Precio</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">25</p>
              <p className="text-[10px] text-gray-500">Stock</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="flex-1 py-2 text-xs font-medium border border-gray-200 rounded-lg">
              Ver en inventario
            </button>
            <button className="flex-1 py-2 text-xs font-medium text-white rounded-lg" style={{ backgroundColor: BRAND.blue }}>
              Publicar online
            </button>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
];

const createTiendaSteps = (): ModuleStep[] => [
  {
    id: 'tienda-intro',
    title: 'Tu Tienda Online',
    description: 'La Vitrina Virtual es tu catálogo público donde los clientes pueden ver y pedir tus productos.',
    hotspotLabel: 'Configura tu tienda',
    content: (
      <PhoneMockup title="Vitrina Virtual">
        <div className="space-y-3">
          <div className="p-3 rounded-xl" style={{ backgroundColor: BRAND.navy }}>
            <p className="text-white font-semibold text-sm">Mi Boutique</p>
            <p className="text-white/70 text-[10px]">clientelo.com.co/mi-boutique</p>
          </div>
          <Tooltip label="Personaliza tu tienda">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 flex items-center gap-2">
              <Layout className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <p className="text-xs font-medium text-blue-800">Personalizar apariencia</p>
                <p className="text-[10px] text-blue-600">Logo, colores, descripción</p>
              </div>
              <ChevronRight className="w-4 h-4 text-blue-400" />
            </div>
          </Tooltip>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Estado:</span>
            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Publicada</span>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'tienda-productos',
    title: 'Productos en catálogo',
    description: 'Selecciona qué productos mostrar en tu tienda online. Puedes activar o desactivar cada uno.',
    hotspotLabel: 'Activa productos',
    content: (
      <PhoneMockup title="Productos Online">
        <div className="space-y-3">
          <p className="text-xs text-gray-500">Productos visibles en tu tienda:</p>
          <div className="space-y-2">
            {MOCK_PRODUCTS.map((p, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <img src={p.image} alt={p.name} className="w-10 h-10 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">{p.name}</p>
                  <p className="text-[10px] text-gray-500">{formatCOP(p.price)}</p>
                </div>
                <Tooltip label={i === 0 ? 'Toggle aquí' : ''}>
                  <button className={`w-10 h-5 rounded-full transition-colors ${i < 2 ? 'bg-green-500' : 'bg-gray-300'}`}>
                    <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${i < 2 ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'tienda-compartir',
    title: 'Compartir enlace',
    description: 'Copia tu enlace único y compártelo por WhatsApp, Instagram o donde quieras.',
    hotspotLabel: 'Copia y comparte',
    content: (
      <PhoneMockup title="Compartir Tienda">
        <div className="space-y-4">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mb-2">
              <Globe className="w-8 h-8 text-white" />
            </div>
            <p className="font-semibold text-gray-900">Tu tienda está lista</p>
          </div>
          <Tooltip label="Click para copiar">
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="text"
                value="clientelo.com.co/mi-boutique"
                readOnly
                className="flex-1 text-xs bg-transparent text-gray-600"
              />
              <button className="p-1.5 bg-white rounded border border-gray-200">
                <Share2 className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </Tooltip>
          <div className="grid grid-cols-3 gap-2">
            {[
              { icon: '💬', label: 'WhatsApp', color: 'bg-green-500' },
              { icon: '📷', label: 'Instagram', color: 'bg-gradient-to-br from-purple-500 to-pink-500' },
              { icon: '📋', label: 'Copiar', color: 'bg-gray-700' },
            ].map((opt, i) => (
              <button key={i} className={`p-3 rounded-lg text-white text-center ${opt.color}`}>
                <span className="text-lg">{opt.icon}</span>
                <p className="text-[10px] mt-1">{opt.label}</p>
              </button>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'tienda-catalogo',
    title: 'Vista del catálogo',
    description: 'Así ven tus clientes tu tienda. Pueden navegar, ver detalles y hacer pedidos.',
    content: (
      <PhoneMockup title="Vista Cliente">
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: BRAND.navy }}>
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">MB</div>
            <div className="text-white">
              <p className="text-xs font-semibold">Mi Boutique</p>
              <p className="text-[10px] opacity-70">Ropa y accesorios</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {MOCK_PRODUCTS.slice(0, 4).map((p, i) => (
              <div key={i} className="bg-gray-50 rounded-lg overflow-hidden">
                <img src={p.image} alt={p.name} className="w-full h-20 object-cover" />
                <div className="p-2">
                  <p className="text-[10px] font-medium text-gray-900 truncate">{p.name}</p>
                  <p className="text-xs font-bold" style={{ color: BRAND.blue }}>{formatCOP(p.price)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
];

const createPedidosSteps = (): ModuleStep[] => [
  {
    id: 'pedidos-intro',
    title: 'Gestión de Pedidos',
    description: 'Aquí ves todos los pedidos que llegan de tu tienda online. Organiza, confirma y entrega.',
    hotspotLabel: 'Ver pedidos',
    content: (
      <PhoneMockup title="Pedidos">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 text-sm">Mis Pedidos</h3>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-medium rounded-full">2 pendientes</span>
          </div>
          <div className="space-y-2">
            {MOCK_ORDERS.map((o, i) => (
              <Tooltip key={i} label={i === 0 ? 'Ver detalles' : ''}>
                <div className={`p-3 rounded-lg border ${i === 0 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-900">{o.id}</span>
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                      o.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {o.status === 'pending' ? 'Pendiente' : 'Confirmado'}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-600">{o.client} · {o.product}</p>
                  <p className="text-xs font-bold text-gray-900 mt-1">{formatCOP(o.total)}</p>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'pedidos-detalle',
    title: 'Detalle del pedido',
    description: 'Ve toda la información del pedido: cliente, productos, total y estado del pago.',
    content: (
      <PhoneMockup title="Pedido PED-4521">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-900">María González</p>
              <p className="text-[10px] text-gray-500">+57 300 123 4567</p>
            </div>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <img src={MOCK_PRODUCTS[0]?.image ?? ''} alt="" className="w-10 h-10 rounded object-cover" />
              <div className="flex-1">
                <p className="text-xs font-medium">2× Camiseta Premium</p>
                <p className="text-[10px] text-gray-500">SKU: CAM-4521</p>
              </div>
              <p className="text-xs font-bold">{formatCOP(90000)}</p>
            </div>
          </div>
          <div className="border-t border-gray-100 pt-2 space-y-1 text-xs">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span><span>{formatCOP(90000)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>IVA (19%)</span><span>{formatCOP(17100)}</span>
            </div>
            <div className="flex justify-between font-bold text-gray-900">
              <span>Total</span><span>{formatCOP(107100)}</span>
            </div>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'pedidos-comprobante',
    title: 'Comprobante de pago',
    description: 'El cliente sube una foto del comprobante de transferencia. Tú verificas y confirmas.',
    hotspotLabel: 'Ver comprobante',
    content: (
      <PhoneMockup title="Comprobante">
        <div className="space-y-3">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-800">Comprobante adjunto</span>
            </div>
            <Tooltip label="Click para ver">
              <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-lg p-4 flex items-center justify-center">
                <div className="text-center">
                  <FileText className="w-8 h-8 text-green-600 mx-auto mb-1" />
                  <p className="text-[10px] text-green-700">comprobante.jpg</p>
                </div>
              </div>
            </Tooltip>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button className="py-2 text-xs font-medium border border-gray-200 rounded-lg flex items-center justify-center gap-1">
              <X className="w-3 h-3" /> Rechazar
            </button>
            <button className="py-2 text-xs font-medium text-white rounded-lg flex items-center justify-center gap-1" style={{ backgroundColor: BRAND.green }}>
              <Check className="w-3 h-3" /> Verificar
            </button>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'pedidos-estados',
    title: 'Estados del pedido',
    description: 'Actualiza el estado según avanza: Pendiente → Confirmado → En camino → Entregado.',
    hotspotLabel: 'Cambiar estado',
    content: (
      <PhoneMockup title="Estado">
        <div className="space-y-3">
          <p className="text-xs text-gray-500">Actualizar estado del pedido:</p>
          <div className="space-y-2">
            {[
              { label: 'Pendiente', icon: '⏳', active: false, done: true },
              { label: 'Confirmado', icon: '✓', active: true, done: false },
              { label: 'En camino', icon: '🚚', active: false, done: false },
              { label: 'Entregado', icon: '📦', active: false, done: false },
            ].map((s, i) => (
              <Tooltip key={i} label={s.active ? 'Estado actual' : ''}>
                <button className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left ${
                  s.active ? 'border-blue-500 bg-blue-50' : s.done ? 'border-green-200 bg-green-50' : 'border-gray-200'
                }`}>
                  <span className="text-lg">{s.icon}</span>
                  <span className={`text-sm ${s.active ? 'text-blue-700 font-medium' : s.done ? 'text-green-700' : 'text-gray-600'}`}>
                    {s.label}
                  </span>
                  {s.done && <Check className="w-4 h-4 text-green-500 ml-auto" />}
                  {s.active && <div className="w-2 h-2 rounded-full bg-blue-500 ml-auto animate-pulse" />}
                </button>
              </Tooltip>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'pedidos-entrega',
    title: 'Programar entrega',
    description: 'Coordina la entrega con el cliente. Agenda fecha, hora y dirección.',
    hotspotLabel: 'Agendar entrega',
    content: (
      <PhoneMockup title="Entrega">
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
            <Truck className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-xs font-medium text-green-800">Entrega programada</p>
              <p className="text-[10px] text-green-600">El cliente espera su pedido</p>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Fecha de entrega</label>
            <Tooltip label="Seleccionar fecha">
              <input
                type="text"
                value="Mié, 30 Oct 2024"
                readOnly
                className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
              />
            </Tooltip>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Dirección</label>
            <input
              type="text"
              value="Cra 15 #82-34, Bogotá"
              readOnly
              className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
            />
          </div>
          <button className="w-full py-2.5 text-xs font-medium text-white rounded-lg flex items-center justify-center gap-1" style={{ backgroundColor: BRAND.blue }}>
            <Calendar className="w-4 h-4" /> Confirmar entrega
          </button>
        </div>
      </PhoneMockup>
    ),
  },
];

const createVentasSteps = (): ModuleStep[] => [
  {
    id: 'ventas-intro',
    title: 'Punto de Venta (POS)',
    description: 'Registra ventas presenciales rápidamente. Busca productos, agrega al carrito y cobra.',
    hotspotLabel: 'Nueva venta',
    content: (
      <PhoneMockup title="Nueva Venta">
        <div className="space-y-3">
          <Tooltip label="Busca productos">
            <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
              <Package className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar producto o escanear..."
                className="flex-1 text-xs bg-transparent placeholder-gray-400"
                readOnly
              />
              <ScanLine className="w-4 h-4 text-gray-400" />
            </div>
          </Tooltip>
          <div className="grid grid-cols-3 gap-2">
            {MOCK_PRODUCTS.map((p, i) => (
              <button key={i} className="p-2 bg-gray-50 rounded-lg text-center hover:bg-gray-100 transition-colors">
                <img src={p.image} alt={p.name} className="w-full h-12 object-cover rounded mb-1" />
                <p className="text-[9px] text-gray-700 truncate">{p.name}</p>
                <p className="text-[10px] font-bold text-gray-900">{formatCOP(p.price)}</p>
              </button>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'ventas-carrito',
    title: 'Carrito de compra',
    description: 'Los productos se agregan al carrito. Ajusta cantidades y ve el total en tiempo real.',
    hotspotLabel: 'Ajusta cantidad',
    content: (
      <PhoneMockup title="Carrito">
        <div className="space-y-3">
          <div className="space-y-2">
            {MOCK_PRODUCTS.slice(0, 2).map((p, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <img src={p.image} alt={p.name} className="w-10 h-10 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{p.name}</p>
                  <p className="text-[10px] text-gray-500">{formatCOP(p.price)}</p>
                </div>
                <Tooltip label={i === 0 ? 'Cambiar cantidad' : ''}>
                  <div className="flex items-center gap-1">
                    <button className="w-6 h-6 rounded bg-gray-200 text-xs font-bold">−</button>
                    <span className="w-6 text-center text-xs font-semibold">{i === 0 ? 2 : 1}</span>
                    <button className="w-6 h-6 rounded bg-gray-200 text-xs font-bold">+</button>
                  </div>
                </Tooltip>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-200 pt-2 space-y-1 text-xs">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatCOP(279000)}</span></div>
            <div className="flex justify-between"><span>IVA (19%)</span><span>{formatCOP(53010)}</span></div>
            <div className="flex justify-between font-bold text-base"><span>Total</span><span>{formatCOP(332010)}</span></div>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'ventas-cliente',
    title: 'Asociar cliente',
    description: 'Busca un cliente existente o crea uno nuevo. Así acumulas historial de compras.',
    hotspotLabel: 'Buscar cliente',
    content: (
      <PhoneMockup title="Cliente">
        <div className="space-y-3">
          <Tooltip label="Busca o crea">
            <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
              <Users className="w-4 h-4 text-gray-400" />
              <input type="text" value="María" readOnly className="flex-1 text-xs bg-transparent" />
            </div>
          </Tooltip>
          <div className="space-y-2">
            {MOCK_CLIENTS.slice(0, 2).map((c, i) => (
              <button key={i} className={`w-full flex items-center gap-2 p-2 rounded-lg border text-left ${i === 0 ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                  {c.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium">{c.name}</p>
                  <p className="text-[10px] text-gray-500">{c.phone}</p>
                </div>
                {i === 0 && <Check className="w-4 h-4 text-blue-600" />}
              </button>
            ))}
          </div>
          <button className="w-full py-2 text-xs text-blue-600 font-medium">+ Crear nuevo cliente</button>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'ventas-pago',
    title: 'Método de pago',
    description: 'Selecciona cómo paga el cliente: efectivo, transferencia, tarjeta, o crédito.',
    hotspotLabel: 'Elige método',
    content: (
      <PhoneMockup title="Pago">
        <div className="space-y-3">
          <p className="text-xs text-gray-500">¿Cómo paga el cliente?</p>
          <div className="space-y-2">
            {[
              { icon: '💵', label: 'Efectivo', selected: true },
              { icon: '📱', label: 'Transferencia', selected: false },
              { icon: '💳', label: 'Tarjeta', selected: false },
              { icon: '📋', label: 'Crédito / Fiado', selected: false },
            ].map((m, i) => (
              <Tooltip key={i} label={m.selected ? 'Seleccionado' : ''}>
                <button className={`w-full flex items-center gap-3 p-3 rounded-lg border ${m.selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <span className="text-xl">{m.icon}</span>
                  <span className={`text-sm ${m.selected ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{m.label}</span>
                  {m.selected && <Check className="w-4 h-4 text-blue-600 ml-auto" />}
                </button>
              </Tooltip>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'ventas-completar',
    title: '¡Venta completada!',
    description: 'La venta se registra, el stock se actualiza y puedes generar un recibo o factura.',
    content: (
      <PhoneMockup title="¡Listo!">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">¡Venta exitosa!</p>
            <p className="text-2xl font-bold mt-1" style={{ color: BRAND.green }}>{formatCOP(332010)}</p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center text-xs">
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="font-bold text-gray-900">3</p>
              <p className="text-gray-500">Productos</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="font-bold text-gray-900">Efectivo</p>
              <p className="text-gray-500">Método</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="flex-1 py-2 text-xs font-medium border border-gray-200 rounded-lg">
              Imprimir recibo
            </button>
            <button className="flex-1 py-2 text-xs font-medium text-white rounded-lg" style={{ backgroundColor: BRAND.blue }}>
              Nueva venta
            </button>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
];

const createClientesSteps = (): ModuleStep[] => [
  {
    id: 'clientes-intro',
    title: 'Gestión de Clientes',
    description: 'Tu base de datos de clientes. Ve historial de compras, segmentos y datos de contacto.',
    hotspotLabel: 'Ver clientes',
    content: (
      <PhoneMockup title="Clientes">
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
            <Users className="w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Buscar cliente..." className="flex-1 text-xs bg-transparent placeholder-gray-400" readOnly />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {['Todos', 'VIP', 'Frecuente', 'Nuevo'].map((seg, i) => (
              <button key={seg} className={`px-3 py-1 text-xs rounded-full whitespace-nowrap ${i === 0 ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-600'}`}>
                {seg}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {MOCK_CLIENTS.map((c, i) => (
              <Tooltip key={i} label={i === 0 ? 'Ver perfil' : ''}>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                    c.segment === 'VIP' ? 'bg-amber-500' : c.segment === 'Frecuente' ? 'bg-blue-500' : 'bg-gray-400'
                  }`}>
                    {c.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{c.name}</p>
                    <p className="text-[10px] text-gray-500">{c.totalCompras} compras</p>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] rounded-full ${
                    c.segment === 'VIP' ? 'bg-amber-100 text-amber-700' : c.segment === 'Frecuente' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {c.segment}
                  </span>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'clientes-perfil',
    title: 'Perfil del cliente',
    description: 'Información completa: datos de contacto, historial de compras y notas.',
    content: (
      <PhoneMockup title="Ana Martínez">
        <div className="space-y-3">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-500 flex items-center justify-center text-white text-xl font-bold">AM</div>
            <p className="font-semibold mt-2">Ana Martínez</p>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-full">VIP</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center text-xs">
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="font-bold text-lg text-gray-900">12</p>
              <p className="text-gray-500">Compras</p>
            </div>
            <div className="p-2 bg-gray-50 rounded-lg">
              <p className="font-bold text-lg text-gray-900">{formatCOP(1540000)}</p>
              <p className="text-gray-500">Total gastado</p>
            </div>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <Smartphone className="w-4 h-4 text-gray-400" />
              <span>+57 320 456 7890</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <Tag className="w-4 h-4 text-gray-400" />
              <span>ana@email.com</span>
            </div>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'clientes-nuevo',
    title: 'Crear nuevo cliente',
    description: 'Agrega un cliente con nombre, teléfono y email. Se guarda en tu base de datos.',
    hotspotLabel: 'Llenar formulario',
    content: (
      <PhoneMockup title="Nuevo Cliente">
        <div className="space-y-3">
          <Tooltip label="Ingresa los datos">
            <div>
              <label className="text-xs font-medium text-gray-700">Nombre completo</label>
              <input type="text" value="Pedro Gómez" readOnly className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white" />
            </div>
          </Tooltip>
          <div>
            <label className="text-xs font-medium text-gray-700">WhatsApp</label>
            <input type="text" value="+57 315 789 0123" readOnly className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Email (opcional)</label>
            <input type="text" value="pedro@email.com" readOnly className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white" />
          </div>
          <button className="w-full py-2.5 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: BRAND.blue }}>
            <UserPlus className="w-4 h-4 inline mr-1" /> Guardar cliente
          </button>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'clientes-historial',
    title: 'Historial de compras',
    description: 'Ve todas las compras del cliente ordenadas por fecha. Accede a facturas y detalles.',
    content: (
      <PhoneMockup title="Historial">
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">AM</div>
            <p className="text-xs font-medium">Ana Martínez · 12 compras</p>
          </div>
          <div className="space-y-2">
            {[
              { date: '25 Oct 2024', items: '2× Camiseta Premium', total: 107100 },
              { date: '18 Oct 2024', items: '1× Reloj Clásico', total: 380800 },
              { date: '10 Oct 2024', items: '3× productos', total: 245000 },
            ].map((h, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-xs font-medium">{h.date}</p>
                  <p className="text-[10px] text-gray-500">{h.items}</p>
                </div>
                <p className="text-xs font-bold">{formatCOP(h.total)}</p>
              </div>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
];

const createCobrosSteps = (): ModuleStep[] => [
  {
    id: 'cobros-intro',
    title: 'Cobros y Créditos',
    description: 'Gestiona saldos pendientes, créditos a clientes y membresías recurrentes.',
    hotspotLabel: 'Ver cobros',
    content: (
      <PhoneMockup title="Cobros">
        <div className="space-y-3">
          <div className="p-3 rounded-xl text-white" style={{ backgroundColor: BRAND.navy }}>
            <p className="text-xs opacity-70">Por cobrar</p>
            <p className="text-2xl font-bold">{formatCOP(1250000)}</p>
            <p className="text-[10px] opacity-60">5 clientes con saldo</p>
          </div>
          <div className="space-y-2">
            {[
              { name: 'Carlos R.', amount: 450000, days: 5 },
              { name: 'Laura G.', amount: 320000, days: 12 },
              { name: 'Pedro M.', amount: 480000, days: 3 },
            ].map((c, i) => (
              <Tooltip key={i} label={i === 0 ? 'Ver detalles' : ''}>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <Wallet className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium">{c.name}</p>
                    <p className="text-[10px] text-gray-500">Hace {c.days} días</p>
                  </div>
                  <p className="text-xs font-bold text-amber-600">{formatCOP(c.amount)}</p>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'cobros-abonar',
    title: 'Registrar abono',
    description: 'Cuando el cliente paga parte de su deuda, registras el abono y se actualiza el saldo.',
    hotspotLabel: 'Registrar pago',
    content: (
      <PhoneMockup title="Abonar">
        <div className="space-y-3">
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <p className="text-xs text-amber-700">Saldo pendiente de Carlos R.</p>
            <p className="text-xl font-bold text-amber-800">{formatCOP(450000)}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Monto a abonar</label>
            <Tooltip label="Ingresa el monto">
              <input type="text" value="$200.000" readOnly className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-center font-semibold" />
            </Tooltip>
          </div>
          <div className="p-2 bg-green-50 rounded-lg text-xs text-green-700 text-center">
            Nuevo saldo: <strong>{formatCOP(250000)}</strong>
          </div>
          <button className="w-full py-2.5 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: BRAND.green }}>
            Registrar abono
          </button>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'cobros-membresias',
    title: 'Membresías',
    description: 'Crea planes de membresía con pagos recurrentes. Ideal para gimnasios, academias y más.',
    hotspotLabel: 'Ver planes',
    content: (
      <PhoneMockup title="Membresías">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Planes activos</h3>
            <button className="text-xs text-blue-600 font-medium">+ Nuevo plan</button>
          </div>
          <div className="space-y-2">
            {[
              { name: 'Plan Básico', price: 80000, members: 15, color: 'bg-gray-100' },
              { name: 'Plan Premium', price: 150000, members: 8, color: 'bg-amber-100' },
              { name: 'Plan VIP', price: 250000, members: 3, color: 'bg-purple-100' },
            ].map((p, i) => (
              <Tooltip key={i} label={i === 1 ? 'Ver miembros' : ''}>
                <div className={`flex items-center gap-2 p-3 rounded-lg ${p.color}`}>
                  <Crown className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="text-xs font-medium">{p.name}</p>
                    <p className="text-[10px] text-gray-500">{p.members} miembros</p>
                  </div>
                  <p className="text-xs font-bold">{formatCOP(p.price)}/mes</p>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'cobros-recordatorio',
    title: 'Recordatorios de pago',
    description: 'Envía recordatorios automáticos por WhatsApp cuando un pago está próximo o vencido.',
    content: (
      <PhoneMockup title="Recordatorios">
        <div className="space-y-3">
          <div className="p-3 bg-green-50 rounded-lg border border-green-100">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">💬</span>
              <span className="text-xs font-medium text-green-800">WhatsApp automático</span>
            </div>
            <p className="text-[10px] text-green-700">
              "Hola Carlos, te recordamos que tienes un saldo pendiente de $450.000. ¿Nos cuentas cuándo puedes pasar?"
            </p>
          </div>
          <div className="space-y-2 text-xs">
            <label className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <input type="checkbox" checked readOnly className="rounded text-blue-600" />
              <span>Enviar 3 días antes del vencimiento</span>
            </label>
            <label className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <input type="checkbox" checked readOnly className="rounded text-blue-600" />
              <span>Enviar el día del vencimiento</span>
            </label>
            <label className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <input type="checkbox" readOnly className="rounded text-blue-600" />
              <span>Enviar si lleva 7 días vencido</span>
            </label>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
];

const createCheckinSteps = (): ModuleStep[] => [
  {
    id: 'checkin-intro',
    title: 'Check-in con QR',
    description: 'Registra asistencia de clientes con un código QR estático. Perfecto para clases, gimnasios y eventos.',
    hotspotLabel: 'Ver QR',
    content: (
      <PhoneMockup title="Check-in">
        <div className="space-y-3 text-center">
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="w-32 h-32 mx-auto bg-white rounded-lg flex items-center justify-center border-2 border-gray-200">
              <QrCode className="w-24 h-24 text-gray-800" />
            </div>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Tu QR de Check-in</p>
            <p className="text-xs text-gray-500">Imprime y coloca en tu local</p>
          </div>
          <Tooltip label="Descargar o imprimir">
            <button className="px-4 py-2 text-xs font-medium text-white rounded-lg" style={{ backgroundColor: BRAND.blue }}>
              Descargar QR
            </button>
          </Tooltip>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'checkin-escanear',
    title: 'Cliente escanea el QR',
    description: 'El cliente abre la cámara de su celular, escanea el QR y se registra automáticamente.',
    content: (
      <PhoneMockup title="Escaneando...">
        <div className="space-y-3 text-center">
          <div className="relative">
            <div className="w-full h-40 bg-gray-900 rounded-xl flex items-center justify-center">
              <div className="w-24 h-24 border-2 border-white/50 rounded-lg relative">
                <div className="absolute inset-0 border-2 border-blue-400 rounded-lg animate-pulse" />
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-blue-400 rounded-tl" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-blue-400 rounded-tr" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-blue-400 rounded-bl" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-blue-400 rounded-br" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500">Apunta la cámara al código QR</p>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'checkin-confirmado',
    title: '¡Check-in confirmado!',
    description: 'El cliente ve una pantalla de confirmación con su nombre y el negocio.',
    content: (
      <PhoneMockup title="¡Listo!">
        <div className="space-y-4 text-center py-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-12 h-12 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">¡Check-in confirmado!</p>
            <p className="text-sm text-gray-600 mt-2">Bienvenida, María</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">Registrado en</p>
            <p className="font-semibold text-gray-900">Gimnasio FitPro</p>
            <p className="text-xs text-gray-500 mt-1">28 Oct 2024 · 8:45 AM</p>
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <Heart className="w-4 h-4 text-red-400" />
            <span>¡Disfruta tu entrenamiento!</span>
          </div>
        </div>
      </PhoneMockup>
    ),
  },
  {
    id: 'checkin-historial',
    title: 'Historial de asistencia',
    description: 'Ve quién hizo check-in, cuándo y con qué frecuencia. Identifica clientes activos.',
    hotspotLabel: 'Ver registros',
    content: (
      <PhoneMockup title="Asistencia">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Hoy · 12 check-ins</h3>
            <button className="text-xs text-blue-600">Ver todos</button>
          </div>
          <div className="space-y-2">
            {[
              { name: 'María González', time: '8:45 AM', plan: 'Premium' },
              { name: 'Carlos Rodríguez', time: '9:15 AM', plan: 'Básico' },
              { name: 'Ana Martínez', time: '10:00 AM', plan: 'VIP' },
            ].map((c, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium">{c.name}</p>
                  <p className="text-[10px] text-gray-500">{c.plan}</p>
                </div>
                <p className="text-xs text-gray-500">{c.time}</p>
              </div>
            ))}
          </div>
        </div>
      </PhoneMockup>
    ),
  },
];

const MODULES: Module[] = [
  {
    id: 'productos',
    title: 'Productos',
    description: 'Crea productos, SKU automático, código de barras, categorías e inventario',
    icon: Package,
    color: 'bg-blue-500',
    duration: '2 min',
    steps: createProductosSteps(),
  },
  {
    id: 'tienda',
    title: 'Tienda Online',
    description: 'Publica tu catálogo en tu Vitrina Virtual y comparte el enlace',
    icon: Globe,
    color: 'bg-green-500',
    duration: '1 min',
    steps: createTiendaSteps(),
  },
  {
    id: 'pedidos',
    title: 'Pedidos',
    description: 'Recibe pedidos online, verifica pagos y gestiona entregas',
    icon: ShoppingCart,
    color: 'bg-amber-500',
    duration: '2 min',
    steps: createPedidosSteps(),
  },
  {
    id: 'ventas',
    title: 'Nueva Venta (POS)',
    description: 'Punto de venta para ventas presenciales rápidas',
    icon: Receipt,
    color: 'bg-purple-500',
    duration: '2 min',
    steps: createVentasSteps(),
  },
  {
    id: 'clientes',
    title: 'Clientes',
    description: 'Gestiona tu base de clientes, segmentos e historial de compras',
    icon: Users,
    color: 'bg-pink-500',
    duration: '1.5 min',
    steps: createClientesSteps(),
  },
  {
    id: 'cobros',
    title: 'Cobros y Membresías',
    description: 'Créditos, saldos pendientes, membresías recurrentes',
    icon: Wallet,
    color: 'bg-indigo-500',
    duration: '2 min',
    steps: createCobrosSteps(),
  },
  {
    id: 'checkin',
    title: 'Check-in QR',
    description: 'Registra asistencia con código QR estático',
    icon: QrCode,
    color: 'bg-teal-500',
    duration: '1 min',
    steps: createCheckinSteps(),
  },
];

export const GuidedWalkthrough: React.FC<GuidedWalkthroughProps> = ({ isOpen, onClose, onSwitchToQuickDemo }) => {
  const [selectedModule, setSelectedModule] = useState<ModuleId | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const module = useMemo(() => 
    selectedModule ? MODULES.find(m => m.id === selectedModule) : null,
  [selectedModule]);

  const currentStep = useMemo(() => 
    module?.steps[currentStepIndex] ?? null,
  [module, currentStepIndex]);

  const progress = useMemo(() => {
    if (!module) return 0;
    return ((currentStepIndex + 1) / module.steps.length) * 100;
  }, [module, currentStepIndex]);

  const handleSelectModule = useCallback((id: ModuleId) => {
    setSelectedModule(id);
    setCurrentStepIndex(0);
  }, []);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(i => i - 1);
    } else {
      setSelectedModule(null);
    }
  }, [currentStepIndex]);

  const handleNext = useCallback(() => {
    if (module && currentStepIndex < module.steps.length - 1) {
      setCurrentStepIndex(i => i + 1);
    }
  }, [module, currentStepIndex]);

  const handleSkip = useCallback(() => {
    setSelectedModule(null);
    setCurrentStepIndex(0);
  }, []);

  const isLastStep = module && currentStepIndex === module.steps.length - 1;

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100" style={{ backgroundColor: BRAND.navy }}>
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-bold text-white truncate">
              {selectedModule ? module?.title : 'Explora Clientelo'}
            </h2>
            <p className="text-xs sm:text-sm text-white/70">
              {selectedModule ? `Paso ${currentStepIndex + 1} de ${module?.steps.length}` : 'Elige qué quieres explorar'}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-2">
            {!selectedModule && (
              <button
                onClick={onSwitchToQuickDemo}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <Play className="w-3.5 h-3.5" />
                Demo rápido
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Progress Bar (only when in module) */}
        {selectedModule && (
          <div className="h-1 bg-gray-200">
            <div 
              className="h-full transition-all duration-300"
              style={{ width: `${progress}%`, backgroundColor: BRAND.blue }}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {!selectedModule ? (
            /* Module Picker */
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                Cada módulo toma 1-2 minutos. Aprende a tu ritmo.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {MODULES.map((m) => {
                  const Icon = m.icon;
                  return (
                    <button
                      key={m.id}
                      onClick={() => handleSelectModule(m.id)}
                      className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all text-left group"
                    >
                      <div className={`w-10 h-10 rounded-lg ${m.color} flex items-center justify-center shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">{m.title}</p>
                          <span className="text-[10px] text-gray-400">{m.duration}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{m.description}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 shrink-0 mt-1" />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Module Step */
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">{currentStep?.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{currentStep?.description}</p>
              </div>
              
              <div className="flex justify-center">
                {currentStep?.content}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-2">
          {selectedModule ? (
            <>
              <button
                onClick={handleSkip}
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
              >
                <SkipForward className="w-4 h-4" />
                <span className="hidden sm:inline">Saltar módulo</span>
                <span className="sm:hidden">Saltar</span>
              </button>
              
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={handleBack}
                  className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                >
                  ← Anterior
                </button>

                {isLastStep ? (
                  <a
                    href="/registro"
                    className="px-4 sm:px-6 py-2 sm:py-2.5 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-lg"
                    style={{ backgroundColor: BRAND.green }}
                  >
                    Empieza gratis
                    <ChevronRight className="w-4 h-4" />
                  </a>
                ) : (
                  <button
                    onClick={handleNext}
                    className="px-4 sm:px-6 py-2 sm:py-2.5 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
                    style={{ backgroundColor: BRAND.blue }}
                  >
                    Siguiente
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={onSwitchToQuickDemo}
                className="sm:hidden px-4 py-2 text-xs font-medium text-white rounded-lg flex items-center gap-1"
                style={{ backgroundColor: BRAND.blue }}
              >
                <Play className="w-3.5 h-3.5" />
                Demo rápido
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuidedWalkthrough;
